import { useQuery } from "@tanstack/react-query";
import CreditService from "services/CreditService";
import useCreditProfileStore from "zustandStores/userCreditProfileStore";

interface CreditProfileData {
  totalAvailableCredits: number | null;
  holdCredits: number | null;
  creditsDateOfExpiry: Date | null;
  showExpiringCredits?: boolean;
  isCreditBetaUser?: boolean;
  hasViewedCreditSystemTnc?: boolean;
}

export const useCreditProfile = (recruiterId?: string) => {
  const { fetchedState, data: storeData } = useCreditProfileStore();

  const query = useQuery<CreditProfileData>({
    queryKey: ["creditProfile", recruiterId],
    queryFn: async () => {
      const response = await CreditService.userCreditProfile(recruiterId);

      // Transform date string to Date object
      const creditsDateOfExpiry = response.creditsDateOfExpiry
        ? new Date(response.creditsDateOfExpiry)
        : null;

      // Calculate if credits are expiring soon
      let showExpiringCredits = false;
      if (creditsDateOfExpiry) {
        const today = new Date();
        const differenceInMillis =
          creditsDateOfExpiry.getTime() - today.getTime();
        const differenceInDays = differenceInMillis / (1000 * 3600 * 24);
        showExpiringCredits = differenceInDays >= 0 && differenceInDays <= 7;
      }

      return {
        ...response,
        creditsDateOfExpiry,
        showExpiringCredits,
        isCreditBetaUser: response.isCreditBetaUser,
        hasViewedCreditSystemTnc: response.hasViewedCreditSystemTnc
      };
    },
    // Only fetch if:
    // 1. recruiterId exists (always fetch for specific recruiter) OR
    // 2. no store data exists (first load)
    enabled: !!recruiterId || fetchedState !== "SUCCESS",
    refetchOnWindowFocus: false
  });

  // Return store data if no recruiterId and store has data
  // Otherwise return query data
  return {
    ...query,
    data: !recruiterId && fetchedState === "SUCCESS" ? storeData : query.data
  };
};
